/**
 * Workflow data model.
 *
 * A workflow is a parameterised deliverable: a block list plus the scope and
 * inputs it runs against. A template is the same object with its inputs
 * declared and unfilled, which is why templates render with no LLM involved.
 *
 * Blocks never carry data — only a description of where to fetch it. That is
 * what keeps every figure on the canvas a real query result and lets the same
 * workflow be re-run for a different title or quarter.
 */

/**
 * `chart` is the general visual: one type, with `options.chartType` choosing
 * line, bar, area or donut. It exists because a fixed type per chart shape
 * means every new visual is an engineering task, and the agent can only ever
 * pick from types that already exist — asking it for a bar chart when there is
 * no `bar` type produces nothing.
 *
 * `timeseries` and `donut` are kept as aliases of it. Saved workflows in
 * localStorage carry those names, so removing them would break documents
 * people already have; the renderer normalises them to `chart` instead.
 */
export type BlockType =
  | 'banner'
  | 'kpis'
  | 'chart'
  | 'timeseries' // alias: chart + chartType 'area'
  | 'donut' //      alias: chart + chartType 'donut'
  | 'split'
  | 'table'
  | 'note'
  | 'text';

export type ChartType = 'line' | 'bar' | 'area' | 'donut';

/**
 * How much of the row a block occupies.
 *
 * The canvas was single-column by design — "no drag, no resize, no grid" — and
 * that was right for a dashboard. A deliverable is a different object: it needs
 * a stat row across the top and two charts side by side, the way a report is
 * actually laid out. The grid is fixed at twelfths and the choices are coarse
 * on purpose, so a workflow can't be dragged into a bespoke layout that stops
 * looking like the rest of them.
 */
export type BlockLayout = 'full' | 'half' | 'third';

export interface DataSource {
  /** Endpoint template; `{{token}}` resolves from scope + inputs. */
  endpoint: string;
  /** Query params; values may also contain tokens. Empty values are dropped. */
  params?: Record<string, string>;
}

export interface Block {
  id: string;
  type: BlockType;
  title: string;
  subtitle?: string;
  /**
   * Where the block's figures come from. Absent only on `text`, which carries
   * authored prose instead — see `content`.
   */
  source?: DataSource;
  /** Column span. Defaults to `full`. */
  layout?: BlockLayout;
  /**
   * Authored prose, for `text` blocks only.
   *
   * This is the one place a block carries content rather than a description of
   * where to fetch it, and the exception is deliberate: prose written by a
   * person cannot misstate a figure, because it isn't a figure. The rule it
   * protects — that no *number* on the canvas was authored — is untouched.
   */
  content?: string;
  /** Per-type configuration — see each block renderer. */
  options?: Record<string, unknown>;
}

export interface WorkflowScope {
  region: string;
  timePeriod: string;
}

export interface WorkflowInputDef {
  key: string;
  label: string;
  /** `search` is a free-text franchise/title match rather than a picked id. */
  type: 'title' | 'platform' | 'search';
  required: boolean;
  placeholder?: string;
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  /** Product line — groups the gallery and labels the tile. */
  category: string;
  inputs: WorkflowInputDef[];
  defaultScope: WorkflowScope;
  blocks: Block[];
}

export interface Workflow {
  id: string;
  name: string;
  templateId?: string;
  scope: WorkflowScope;
  inputs: Record<string, string>;
  blocks: Block[];
  createdAt: string;
  updatedAt: string;
}

// ─── Token resolution ──────────────────────────────────────────────────────

const TOKEN = /\{\{(\w+)\}\}/g;

/** Values a block's tokens can resolve against. */
export function tokenContext(workflow: Pick<Workflow, 'scope' | 'inputs'>) {
  return {
    region: workflow.scope.region,
    timePeriod: workflow.scope.timePeriod,
    ...workflow.inputs,
  } as Record<string, string>;
}

/** Replace `{{token}}` occurrences; unknown tokens resolve to ''. */
export function resolveTokens(value: string, context: Record<string, string>): string {
  return value.replace(TOKEN, (_, key: string) => context[key] ?? '');
}

/**
 * True when a block has everything it needs to fetch.
 *
 * Two classes of token, deliberately treated differently:
 *
 * - **In the endpoint** — always required. `/api/titles//revenue` is not a
 *   request worth making.
 * - **In the params** — required only when the token names a declared required
 *   input (`requiredKeys`). Scope params are the opposite: an empty `region`
 *   means "all regions" and is dropped by `blockUrl`, which is correct.
 *
 * That distinction is load-bearing. Franchise Assessment, Catalogue Review and
 * Genre Performance carry their subject as a *param* (`search`, `platform`)
 * against a constant endpoint, so an endpoint-only check calls them ready with
 * no subject chosen — the param is dropped and the block renders the entire
 * catalogue under a heading that claims it is one franchise. The figures are
 * real; they answer a question nobody asked. Passing `requiredKeys` makes the
 * block wait for its subject instead.
 */
export function isBlockReady(
  block: Block,
  context: Record<string, string>,
  requiredKeys: readonly string[] = []
): boolean {
  /* A sourceless block (text) fetches nothing, so it is always ready. */
  if (!block.source) return true;

  const endpointTokens = block.source.endpoint.match(TOKEN) ?? [];
  const paramTokens = Object.values(block.source.params ?? {}).flatMap(
    (value) => value.match(TOKEN) ?? []
  );

  const needed = [
    ...endpointTokens,
    ...paramTokens.filter((raw) => requiredKeys.includes(raw.slice(2, -2))),
  ];

  return needed.every((raw) => Boolean(context[raw.slice(2, -2)]));
}

/**
 * Build the request URL for a block.
 *
 * Params resolving to an empty string are dropped rather than sent — matching
 * the leaderboard convention where an absent filter means "all", not "none".
 */
export function blockUrl(block: Block, context: Record<string, string>): string {
  if (!block.source) return '';
  const endpoint = resolveTokens(block.source.endpoint, context);
  const search = new URLSearchParams();
  for (const [key, raw] of Object.entries(block.source.params ?? {})) {
    const value = resolveTokens(raw, context);
    if (value) search.set(key, value);
  }
  const query = search.toString();
  return query ? `${endpoint}?${query}` : endpoint;
}

// ─── Construction ──────────────────────────────────────────────────────────

let blockCounter = 0;

export function newBlockId(): string {
  blockCounter += 1;
  return `blk_${Date.now().toString(36)}_${blockCounter}`;
}

/** Instantiate a workflow from a template, with fresh block ids. */
export function workflowFromTemplate(
  template: WorkflowTemplate,
  inputs: Record<string, string> = {},
  scope?: Partial<WorkflowScope>
): Workflow {
  const now = new Date().toISOString();
  return {
    id: `wf_${Date.now().toString(36)}`,
    name: template.name,
    templateId: template.id,
    scope: { ...template.defaultScope, ...scope },
    inputs,
    blocks: template.blocks.map((b) => ({ ...b, id: newBlockId() })),
    createdAt: now,
    updatedAt: now,
  };
}

/** An empty workflow — the destination of the "describe your workflow" box. */
export function blankWorkflow(name = 'New workflow'): Workflow {
  const now = new Date().toISOString();
  return {
    id: `wf_${Date.now().toString(36)}`,
    name,
    scope: { region: '', timePeriod: 'all' },
    inputs: {},
    blocks: [],
    createdAt: now,
    updatedAt: now,
  };
}

// ─── Block operations (the agent's edit vocabulary) ────────────────────────

export type BlockOperation =
  | {
      op: 'add';
      block: Omit<Block, 'id'>;
      /** Insert directly after this block. */
      afterId?: string;
      /**
       * Where to place it when no `afterId` is given.
       *
       * Without this, `add` could only append — so an agent asked for "a banner
       * on top" had no operation capable of expressing it, and couldn't correct
       * itself with a follow-up `move` either, because the client assigns the
       * id and it has nothing to reference. Defaults to `end`, except for a
       * banner, which is the document's cover and so defaults to `start`.
       */
      at?: 'start' | 'end';
    }
  | { op: 'remove'; blockId: string }
  | { op: 'replace'; blockId: string; block: Omit<Block, 'id'> }
  | { op: 'move'; blockId: string; delta: number }
  /**
   * Bind the workflow's subject — the thing `{{titleId}}` / `{{platform}}` /
   * `{{search}}` resolve from.
   *
   * Every other operation edits the block list. This one exists because a
   * workflow started from the describe box has no template, so it has no
   * subject and no way to acquire one: the canvas only renders a subject
   * picker for a template's declared input, and the agent had no operation
   * that reached `inputs`. Ask "which genres earn the most on Netflix?" and
   * the agent would put Netflix in every block's params, then correctly refuse
   * a banner — because `{{platform}}` resolved to nothing. It was right about
   * a hole rather than wrong about Netflix.
   */
  | { op: 'setInput'; key: string; value: string }
  /**
   * Change the workflow's scope — which region and which period every block
   * queries against.
   *
   * The canvas used to carry dropdowns for these. They were removed, so this
   * is the only way scope can change: without it a document would be frozen at
   * "all regions, all time" forever, and re-running the same structure against
   * a different period is half the point of a workflow.
   */
  | { op: 'setScope'; key: 'region' | 'timePeriod'; value: string };

/**
 * Apply operations immutably. The agent emits these; the UI's own controls
 * (remove, reorder) go through the same path, so undo works identically for
 * both and there is only one way the document can change.
 */
export function applyOperations(workflow: Workflow, operations: BlockOperation[]): Workflow {
  let blocks = [...workflow.blocks];
  const inputs = { ...workflow.inputs };
  const scope = { ...workflow.scope };

  for (const operation of operations) {
    switch (operation.op) {
      case 'add': {
        const block: Block = { ...operation.block, id: newBlockId() };
        const after = operation.afterId
          ? blocks.findIndex((b) => b.id === operation.afterId)
          : -1;

        if (after >= 0) {
          blocks.splice(after + 1, 0, block);
          break;
        }

        /* A banner is the cover, so it leads unless asked otherwise — the
           common case is right without the agent having to remember. */
        const at = operation.at ?? (block.type === 'banner' ? 'start' : 'end');
        if (at === 'start') blocks.unshift(block);
        else blocks.push(block);
        break;
      }
      case 'remove':
        blocks = blocks.filter((b) => b.id !== operation.blockId);
        break;
      case 'replace':
        blocks = blocks.map((b) =>
          b.id === operation.blockId ? { ...operation.block, id: b.id } : b
        );
        break;
      case 'move': {
        const from = blocks.findIndex((b) => b.id === operation.blockId);
        if (from < 0) break;
        const to = Math.max(0, Math.min(blocks.length - 1, from + operation.delta));
        blocks.splice(to, 0, blocks.splice(from, 1)[0]);
        break;
      }
      case 'setInput': {
        const value = operation.value?.trim() ?? '';
        /* An empty value clears the key rather than binding the subject to an
           empty string, which would leave `{{platform}}` resolving to nothing
           while still looking bound. */
        if (value) inputs[operation.key] = value;
        else delete inputs[operation.key];
        break;
      }
      case 'setScope': {
        const value = operation.value?.trim() ?? '';
        /* Region falls back to '' (meaning all regions) and period to 'all',
           so clearing either returns it to the widest scope rather than to an
           empty string no query understands. */
        if (operation.key === 'region') scope.region = value;
        else scope.timePeriod = value || 'all';
        break;
      }
    }
  }

  return { ...workflow, blocks, inputs, scope, updatedAt: new Date().toISOString() };
}
